import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Image as ImageIcon, Video, X, Loader2 } from 'lucide-react';
import { useEventPresignedUrl } from '../../hooks/useEventPresignedUrl';
import type { Event } from '../../types';

interface EventMediaModalProps {
  event: Event | null;
  mediaType: 'video' | 'image';
  onClose: () => void;
  onMediaTypeChange?: (type: 'video' | 'image') => void;
  videoUrl?: string | null;
}

/** One event, with its snapshot and recording in an accessible full-screen viewer. */
export const EventMediaModal: React.FC<EventMediaModalProps> = ({
  event, mediaType, onClose, onMediaTypeChange, videoUrl,
}) => {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [selectedMedia, setSelectedMedia] = useState(mediaType);
  const [failedMedia, setFailedMedia] = useState('');
  const snapshot = event?.s3_url || event?.event_snapshot_url || event?.event_thumbnail_url;
  const snapshotType = event?.s3_url || event?.event_snapshot_url ? 'snapshot' : 'thumbnail';
  const recording = videoUrl === undefined ? event?.event_video_url : videoUrl;
  const hasVideo = !!recording;
  const requestedType = onMediaTypeChange ? mediaType : selectedMedia;
  const activeType = requestedType === 'video' && hasVideo ? 'video' : 'image';
  const changeMedia = (type: 'image' | 'video') => { setSelectedMedia(type); onMediaTypeChange?.(type); };
  const onPrevious = activeType === 'video' && snapshot ? () => changeMedia('image') : undefined;
  const onNext = activeType === 'image' && hasVideo ? () => changeMedia('video') : undefined;
  const callbacks = useRef({ onClose, onPrevious, onNext });
  callbacks.current = { onClose, onPrevious, onNext };
  const directUrl = activeType === 'video' ? recording : snapshot;
  const { presignedUrl, isLoading, error } = useEventPresignedUrl(
    event?.event_id, activeType === 'video' ? 'video' : snapshotType, directUrl,
  );
  const mediaKey = `${event?.event_id}:${activeType}:${presignedUrl}`;
  const isOpen = !!event;
  useEffect(() => { setSelectedMedia(mediaType); }, [event?.event_id, mediaType]);
  useEffect(() => {
    const player = video.current;
    return () => {
      if (player) { player.pause(); player.removeAttribute('src'); player.load(); }
    };
  }, [mediaKey]);

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    close.current?.focus();
    const keydown = (e: KeyboardEvent) => {
      const current = callbacks.current;
      if (e.key === 'Escape') { e.preventDefault(); current.onClose(); return; }
      const target = e.target as HTMLElement;
      // Keep native seeking/volume shortcuts while the video has focus.
      const nativeControls = ['VIDEO', 'INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
      if (!nativeControls && !e.altKey && !e.ctrlKey && !e.metaKey) {
        if (e.key === 'ArrowLeft' && current.onPrevious) { e.preventDefault(); current.onPrevious(); }
        if (e.key === 'ArrowRight' && current.onNext) { e.preventDefault(); current.onNext(); }
      }
      if (e.key === 'Tab') {
        const items = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), video[controls], [href], [tabindex="0"]') || []);
        const first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = overflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  if (!event) return null;
  const mediaFailed = !!error || failedMedia === mediaKey;
  const iconButton = 'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-25 disabled:cursor-not-allowed';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950/95 text-white backdrop-blur-md"
      role="dialog" aria-modal="true" aria-labelledby={titleId} ref={dialog}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <h2 id={titleId} className="font-semibold">Event #{event.event_id}</h2>
          <p className="mt-0.5 truncate text-xs text-slate-400">{event.camera_name || (event.event_camera ? `Camera ${event.event_camera}` : 'Camera event')}
            {event.event_tag && ` · ${event.event_tag.replace(/_/g, ' ')}`}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-white/10 bg-white/5 p-1" role="group" aria-label="Event media">
            <button type="button" aria-pressed={activeType === 'image'} disabled={!snapshot} onClick={() => changeMedia('image')}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${activeType === 'image' ? 'bg-white text-slate-950' : 'text-slate-300 hover:bg-white/10'}`}>
              <ImageIcon size={16} /> Snapshot
            </button>
            <button type="button" aria-pressed={activeType === 'video'} disabled={!hasVideo} onClick={() => changeMedia('video')}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm disabled:opacity-35 ${activeType === 'video' ? 'bg-white text-slate-950' : 'text-slate-300 hover:bg-white/10'}`}>
              <Video size={16} /> Video
            </button>
          </div>
          <button type="button" ref={close} className={iconButton} onClick={onClose} aria-label="Close event viewer"><X size={20} /></button>
        </div>
      </header>
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-12 py-4 sm:px-20">
        <button type="button" className={`${iconButton} absolute left-2 z-10 sm:left-5`} onClick={onPrevious}
          disabled={!onPrevious} aria-label="Show snapshot"><ChevronLeft size={25} /></button>
        {isLoading ? <div role="status" className="flex flex-col items-center gap-3 text-sm text-slate-300"><Loader2 size={28} className="animate-spin" />Loading event media…</div>
          : mediaFailed ? <p role="alert" className="text-center text-sm text-slate-300">Unable to load this {activeType === 'video' ? 'recording' : 'snapshot'}. You can choose another view or close the viewer.</p>
            : !presignedUrl ? <p className="text-center text-sm text-slate-400">The snapshot is not available yet.</p>
              : activeType === 'video' ? <video ref={video} key={mediaKey} src={presignedUrl} controls autoPlay playsInline aria-label={`Recording for event ${event.event_id}`}
                  className="max-h-full max-w-full rounded-lg" onError={() => setFailedMedia(mediaKey)}>
                  Your browser does not support video playback.
                </video>
                : <img key={mediaKey} src={presignedUrl} alt={`Snapshot for event ${event.event_id}`} className="max-h-full max-w-full object-contain"
                    onError={() => setFailedMedia(mediaKey)} />}
        <button type="button" className={`${iconButton} absolute right-2 z-10 sm:right-5`} onClick={onNext}
          disabled={!onNext} aria-label="Show event video"><ChevronRight size={25} /></button>
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 px-4 py-3 text-xs text-slate-400 sm:px-6">
        <span aria-live="polite">{hasVideo ? `${activeType === 'image' ? 'Snapshot · 1' : 'Video · 2'} of 2` : 'Snapshot · Video is being prepared'}</span>
        <span>← → Switch media · Esc Close</span>
      </footer>
    </div>, document.body,
  );
};

export default EventMediaModal;
